(() => {
  const roots = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function getState(root) {
    const track = root.querySelector('[data-ms-search-cert-track]');
    const items = [...root.querySelectorAll('[data-ms-search-cert-item]')]
      .filter(item => item.querySelector('img.ms-search-cert__image[src]'));
    const previous = root.querySelector('[data-ms-search-cert-previous]');
    const next = root.querySelector('[data-ms-search-cert-next]');
    const pager = root.querySelector('[data-ms-search-cert-pager]');
    const dots = root.querySelector('[data-ms-search-cert-dots]');
    const count = root.querySelector('[data-ms-search-cert-count]');
    if (!track || !items.length || !previous || !next || !pager || !dots || !count) return null;
    return { track, items, previous, next, pager, dots, count };
  }

  function metrics(state) {
    const trackStyle = getComputedStyle(state.track);
    const gap = Number.parseFloat(trackStyle.gap) || 0;
    const itemWidth = state.items[0].getBoundingClientRect().width;
    const step = itemWidth + gap;
    const visible = Math.max(1, Math.floor((state.track.clientWidth + gap) / step));
    const max = Math.max(0, state.items.length - visible);
    const index = Math.max(0, Math.min(max, Math.round(state.track.scrollLeft / step)));
    return { gap, step, visible, max, index };
  }

  function render(root, state) {
    const { max, index } = metrics(state);
    const total = max + 1;
    if (state.dots.children.length !== total) {
      state.dots.replaceChildren();
      for (let page = 0; page < total; page += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ms-search-cert__dot';
        dot.dataset.msSearchCertDot = String(page);
        dot.setAttribute('aria-label', state.track.dataset.pageLabelTemplate.replace('__page_number__', String(page + 1)));
        state.dots.append(dot);
      }
    }
    [...state.dots.children].forEach((dot, page) => dot.setAttribute('aria-current', String(page === index)));
    state.previous.disabled = index === 0;
    state.next.disabled = index === max;
    state.previous.hidden = max === 0;
    state.next.hidden = max === 0;
    state.pager.hidden = max === 0;
    state.count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
    root.dataset.msSearchCertPage = String(index + 1);
  }

  function goTo(root, state, page) {
    const { step, max } = metrics(state);
    const destination = Math.max(0, Math.min(max, page));
    state.track.scrollTo({ left: step * destination, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    root.dataset.msSearchCertPage = String(destination + 1);
    state.previous.disabled = destination === 0;
    state.next.disabled = destination === max;
  }

  function mount(root) {
    if (roots.has(root)) return;
    const state = getState(root);
    if (!state) return;
    const controller = new AbortController();
    const onClick = event => {
      const button = event.target.closest('[data-ms-search-cert-previous], [data-ms-search-cert-next], [data-ms-search-cert-dot]');
      if (!button || !root.contains(button)) return;
      const current = Math.max(0, Number(root.dataset.msSearchCertPage || 1) - 1);
      if (button.hasAttribute('data-ms-search-cert-previous')) goTo(root, state, current - 1);
      else if (button.hasAttribute('data-ms-search-cert-next')) goTo(root, state, current + 1);
      else goTo(root, state, Number(button.dataset.msSearchCertDot));
    };
    const onKeydown = event => {
      if (!state.track.contains(event.target)) return;
      const current = Math.max(0, Number(root.dataset.msSearchCertPage || 1) - 1);
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(root, state, current + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(root, state, current - 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        goTo(root, state, 0);
      } else if (event.key === 'End') {
        event.preventDefault();
        goTo(root, state, metrics(state).max);
      }
    };
    const onScroll = () => window.requestAnimationFrame(() => render(root, state));
    root.addEventListener('click', onClick, { signal: controller.signal });
    state.track.addEventListener('keydown', onKeydown, { signal: controller.signal });
    state.track.addEventListener('scroll', onScroll, { passive: true, signal: controller.signal });
    const observer = new ResizeObserver(() => render(root, state));
    observer.observe(state.track);
    for (const item of state.items) observer.observe(item);
    roots.set(root, { state, controller, observer });
    render(root, state);
  }

  function unmount(root) {
    const instance = roots.get(root);
    if (!instance) return;
    instance.controller.abort();
    instance.observer.disconnect();
    roots.delete(root);
  }

  function scan(scope = document) {
    if (scope.matches?.('[data-ms-search-cert]')) mount(scope);
    scope.querySelectorAll?.('[data-ms-search-cert]').forEach(mount);
  }

  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.('[data-ms-search-cert]')) unmount(event.target);
    event.target.querySelectorAll?.('[data-ms-search-cert]').forEach(unmount);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
})();
